import * as React from 'react';
import {useState, useEffect} from 'react';
import Badge from 'react-bootstrap/Badge';
import Placeholder from 'react-bootstrap/Placeholder';
import Table from 'react-bootstrap/Table';
import {ArrowLeft} from '../common/btns';
import {date, orderItem as oi} from 'ka-erp-data';
import * as api from '../api';
import {receiverName} from '../lib/order';
import {ItemType} from '../../lib/utils';

const headerCss = require('../common/header.css').default;
const css = require('./order-detail.css').default;

export default function({order, onDone}: {
    order: order,
    onDone: () => void,
}) {
    const [pitems, setPitems] = useState<oip[] | 'loading'>('loading');

    useEffect(() => {
        if (order.items.some(oi.anotherOrder)) {
            api.orderItemPending.getByOriOrder(['order', 'oriSeq', 'qty', 'classCnt', 'giftQty', 'cancelDate'], [order.id])
            .then(items => setPitems(items));
        } else {
            setPitems([]);
        }
    }, []);

    return (
    <>
        <div id={headerCss.header}>
            <ArrowLeft width={25} height={25} onClick={onDone}/>
            <div>訂單明細</div>
        </div>

        <div id={css.head}>
            <table id='info'>
                <tbody>
                    <tr>
                        <td>外註</td>
                        <td className='num'>{order.memo}</td>
                    </tr>
                    <tr>
                        <td>建單日期</td>
                        <td className='num'>{date.toDate(order.createDate).toLocaleDateString()}</td>
                    </tr>
                    <tr>
                        <td>預計出貨日</td>
                        <td className='num'>{date.toDate(deliverDate(order)).toLocaleDateString()}</td>
                    </tr>
                    {(() => {
                        if (receiverName(order)) {
                            return (
                            <tr>
                                <td>收件人</td>
                                <td>{receiverName(order)}&nbsp;{order.address}</td>
                            </tr>);
                        }
                    })()}
                    <tr>
                        <td>訂單編號</td>
                        <td className='num'>{order.id}</td>
                    </tr>
                    <tr>
                        <td>地址</td>
                        <td>{order.address}</td>
                    </tr>
                    <tr>
                        <td>包裹狀況</td>
                        <td><PkgStatus pkgs={order.pkgs}/></td>
                    </tr>
                </tbody>
            </table>
            <h4>
                {(() => {
                    if (order.outOfStock) {
                        return <Badge bg='danger'>缺貨中</Badge>;
                    }
                })()}
            </h4>
        </div>
        
        <Table id={css.body} striped>
            <thead>
                <tr>
                    <td>產品</td>
                    <td>數量</td>
                    <td>備註</td>
                </tr>
            </thead>
            <tbody>
                {(() => {
                    return order.items.map((i, idx) => (
                    <tr key={idx}>
                        <td>
                            {(() => {
                                if (i.product === 'loading') {
                                    return <Placeholder xs={10}/>;
                                } else {
                                    return i.product.name;
                                }
                            })()}
                        </td>
                        <td className='num_bold'>
                            <Qty oi={i}
                                oip={pitems === 'loading' ? 'loading' : pitems.filter(pi => pi.oriSeq === i.seq)[0] || null}/>
                        </td>
                        <td>
                            <Memo oi={i}
                                oip={pitems === 'loading' ? 'loading' : pitems.filter(pi => pi.oriSeq === i.seq)[0] || null}/>
                        </td>
                    </tr>));
                })()}
            </tbody>
        </Table>
    </>);
}

function PkgStatus({pkgs}: {
    pkgs: order['pkgs'],
}) {
    if (pkgs === 'loading') {
        return <>載入中</>
    } else {
        if (pkgs.length === 0) {
            return <>尚未打包</>
        } else {
            return (
            <>
                共 {pkgs.length} 件 /
                已包 {pkgs.filter(pkg.packed).length} 件
            </>);
        }
    }
}

function Qty({oi, oip}: {
    oi: ItemType<order['items']>,
    oip: oip | null | 'loading',
}) {
    if (oip === null) {
        return <>{`${oi.qty/oi.classCnt} * ${oi.classCnt} + ${oi.giftQty}`}</>;
    } else if (oip === 'loading') {
        return <Placeholder xs={10}/>;
    } else {
        return <>{`${oip.qty/oip.classCnt} * ${oip.classCnt} + ${oip.giftQty}`}</>;
    }
}

function Memo({oi, oip}: {
    oi: ItemType<order['items']>,
    oip: oip | null | 'loading',
}) {
    return (
    <>
        <div>{oi.memo || ''}</div>
        <div>{(() => {
            if (oip === null) {
                return '';
            } else if (oip === 'loading') {
                return <Placeholder xs={10}/>;
            } else {
                if (oip.order) {
                    return '缺貨已出';
                } else if (oip.cancelDate) {
                    return '取消';
                } else {
                    return '缺貨';
                }
            }
        })()}</div>
    </>);
}

type order = {
    id: string,
    _id: number,
    createDate: date,           // 建立日期
    billDate: date,
    deliverDate: date | null,   // 預計出貨日
    receiverId: string | null,
    receiverName: string | null,
    address: string,
    memo: string | null,
    outOfStock: boolean,
    pkgs: pkg[] | 'loading',
    items: {
        _id: number,
        seq: number,
        product: {
            id: string,
            name: string,
        } | 'loading',
        qty: number,
        classCnt: number,
        giftQty: number | null,
        memo: string | null,
    }[],
}

export function deliverDate(order: order) {
    return order.deliverDate || order.billDate;
}

type pkg = {
    order: string,
    logistic: string,
    items: {
        packDate: date | null,
    }[],
}

namespace pkg {
    export function packed(pkg: pkg) {
        return pkg.items.some(i => i.packDate !== null);
    }
}

type oip = {
    order: string | null,
    oriSeq: number,
    qty: number,
    classCnt: number,
    giftQty: number | null,
    cancelDate: date | null,
}
